import React, { useContext, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import InputBase from '@material-ui/core/InputBase';
import { preloadMicRecorder } from '../lib/micRecorderShim';

// Ícones
import DashboardIcon from '@material-ui/icons/Dashboard';
import HomeIcon from '@material-ui/icons/Home';
import ContactsIcon from '@material-ui/icons/Contacts';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import BuildIcon from '@material-ui/icons/Build';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CachedIcon from '@material-ui/icons/Cached';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import MenuIcon from '@material-ui/icons/Menu';
// Ícones oficiais @material-ui/icons (abrir/fechar menu lateral)
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ChatIcon from '@material-ui/icons/Chat';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ForumIcon from '@material-ui/icons/Forum';
import SearchIcon from '@material-ui/icons/Search';
import MessageIcon from '@material-ui/icons/Message';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import BoltIcon from '@mui/icons-material/Bolt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ScheduleIcon from '@material-ui/icons/Schedule';
import EventIcon from '@material-ui/icons/Event';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import DateRangeIcon from '@material-ui/icons/DateRange';
import SmartFormIcon from '../components/icons/SmartFormIcon';
import QueueIcon from '@material-ui/icons/Queue';
import ListIcon from '@material-ui/icons/List';
import SettingsIcon from '@material-ui/icons/Settings';
import ReceiptIcon from '@material-ui/icons/Receipt';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import BusinessIcon from '@material-ui/icons/Business';
import PaletteIcon from '@material-ui/icons/Palette';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import CategoryIcon from '@material-ui/icons/Category';
import TuneIcon from '@material-ui/icons/Tune';
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import ExtensionIcon from '@material-ui/icons/Extension';
import AppsIcon from '@material-ui/icons/Apps';
import HubIcon from '@mui/icons-material/Hub';
import HandymanIcon from '@mui/icons-material/Handyman';
import { i18n } from '../translate/i18n';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
// import HistoryIcon from '@material-ui/icons/History';
import CampaignIcon from '@mui/icons-material/Campaign';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimerIcon from '@material-ui/icons/Timer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CableIcon from '@mui/icons-material/Cable';
import SecurityIcon from '@material-ui/icons/Security';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SendIcon from '@material-ui/icons/Send';
import AddIcon from '@material-ui/icons/Add';
import { SiMeta } from 'react-icons/si';

import useSupportLinks from '../hooks/useSupportLinks';
import { AuthContext } from '../context/Auth/AuthContext';
import { usePlanPermissions } from '../context/PlanPermissionsContext.jsx';
import NotificationsDrawer from '../components/NotificationsDrawer/index.jsx';
import { APP_PATHS } from '../constants/appPaths';
import { updateMyProfile } from '../services/userService';
import NotificationModal from '../components/NotificationModal';
import BlockedModal from '../components/BlockedModal';
import TrialEndingModal from '../components/TrialEndingModal';
import TrialBanner from '../components/TrialBanner';
import AiCreditsAlert from '../components/AiCreditsAlert';
import {
  shouldShowTrialEndingModal,
  wasTrialEndingModalShownToday,
  markTrialEndingModalShownToday,
} from '../utils/trialEndingUtils';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import PwaHeadUpdater from '../components/PwaHeadUpdater';
import PwaReinstallModal from '../components/PwaReinstallModal';
import SearchTicketModal from '../components/SearchTicketModal/index.jsx';
import InternalChatWidget from '../components/InternalChatWidget/index.jsx';
import TeamGoalChip from '../components/TeamGoalChip';
import NextMeetingChip from '../components/NextMeetingChip';
import TopBarHelpMenu from '../components/TopBarHelpMenu';
import AiCreditsBadge from '../components/AiCreditsBadge';
import NewTicketModal from '../components/NewTicketModal';
import { TicketsContext } from '../context/Tickets/TicketsContext';
import UsersPresencePanel from '../components/UsersPresencePanel';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { getBackendUrl } from '../config';
import {
  SUBSCRIPTION_PATH_BY_SECTION,
  SUBSCRIPTION_SECTIONS,
  isSubscriptionAreaPath,
} from '../pages/Subscription/constants';
import { unsubscribeAndUnregister } from '../services/pushSubscriptionService';
import { canManageSubscription } from '../utils/subscriptionAccess';
import logo from '../assets/logo.png';
import useStyles, {
  collapsedDrawerWidth,
  expandedDrawerWidth,
  TRIAL_BANNER_HEIGHT,
} from './styles';
import usePermission from '../hooks/usePermission';

const backendUrl = getBackendUrl();

/**
 * Chave do submenu por caminho do item pai.
 *
 * Fonte única de propósito: o mesmo mapa decide qual grupo expande e qual
 * conteúdo o popover do menu encolhido mostra. Antes eram duas listas com os
 * mesmos pares, escritas à mão em lugares diferentes, e elas divergiram: a
 * Agenda entrou só na que ABRE o popover, então no menu encolhido ele abria
 * vazio. Item pai novo só precisa entrar aqui.
 */
const SUBMENU_KEY_BY_PATH = {
  '/super-admin': 'superAdmin',
  '/sales': 'vendas',
  '/marketing': 'marketing',
  '/contacts': 'contatos',
  '/tickets': 'atendimentos',
  '/automations': 'automacoes',
  '/crm': 'crm',
  '/book/appointments': 'agenda',
  [APP_PATHS.TOOLS]: 'ferramentas',
};

const LoggedInLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();

  // Aquece o script de gravação de áudio em idle: fora do caminho crítico do
  // login, mas pronto antes da primeira gravação (evita atraso/hiccup ao gravar).
  useEffect(() => {
    preloadMicRecorder();
  }, []);
  const [drawerExpanded, setDrawerExpanded] = useState(true);
  const drawerInitialized = useRef(false);
  const [expandedMenus, setExpandedMenus] = useState(() => ({
    sistema: location.pathname.startsWith('/system') || location.pathname.startsWith('/sistema'),
    superAdmin: location.pathname.startsWith('/super-admin'),
    vendas: location.pathname.startsWith('/sales') || location.pathname.startsWith('/vendas'),
    marketing:
      location.pathname.startsWith('/marketing') || location.pathname.startsWith('/campanhas'),
    contatos:
      location.pathname.startsWith('/contacts') || location.pathname.startsWith('/contatos'),
    // Conversas virou item próprio no menu (fora de Atendimento), mas
    // continua em /tickets/conversation — sem excluir esse prefixo aqui,
    // Atendimento aparecia expandido/ativo mesmo estando em Conversas.
    atendimentos:
      (location.pathname.startsWith('/tickets') &&
        !location.pathname.startsWith('/tickets/conversation')) ||
      location.pathname.startsWith('/chats'),
    automacoes: location.pathname.startsWith('/automations'),
    crm: location.pathname.startsWith('/crm'),
    tasks: location.pathname.startsWith('/tasks'),
    agenda: location.pathname.startsWith('/book'),
    ferramentas: location.pathname.startsWith(APP_PATHS.TOOLS),
  }));
  const drawerWidth = isMobile
    ? expandedDrawerWidth
    : drawerExpanded
      ? expandedDrawerWidth
      : collapsedDrawerWidth;
  const showMenuLabels = drawerWidth > collapsedDrawerWidth || isMobile;
  const primaryColor = theme.palette.primary.main || '#3b82f6';
  const classes = useStyles({
    drawerWidth,
    primaryColor,
    sistemaExpanded: expandedMenus.sistema,
    superAdminExpanded: expandedMenus.superAdmin,
  });

  const { user, handleLogout, loading, isMobileSession } = useContext(AuthContext);
  const { allowed: canReadUsers } = usePermission('users:read');
  const { allowed: canCreateTicket } = usePermission('tickets:create');
  const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);
  const {
    planActive,
    hasApp,
    loading: planLoading,
    trialActive,
    trialExpired,
    trialExpiringSoon,
    openAi: planOpenAi,
    integrations: planIntegrations,
    schedules: planSchedules,
    internalChat: planInternalChat,
    tasks: planTasks,
    products: planProducts,
    appointments: planAppointments,
    mcp: planMcp,
    allowAiPlatformCredits,
  } = usePlanPermissions();
  const userPermissions = useMemo(
    () => new Set(Array.isArray(user?.permissions) ? user.permissions : []),
    [user?.permissions]
  );
  // Feature de plano: aparece se o plano tem a flag (ou super). Sem vazamento de
  // "profile === admin" — admin sem o plano tomaria 403 no backend (requirePlanFeature).
  const userCanUseTasks = Boolean(user?.super) || planTasks;
  const userCanUseAgenda = Boolean(user?.super) || planAppointments;
  // Produtos é feature de plano (Plan.useProducts) — alinhado ao gate do backend
  // requirePlanFeature("useProducts"), que libera só super. Sem o vazamento de
  // "profile === admin" que tasks tem (admin sem o plano tomaria 403).
  const userCanUseProducts = Boolean(user?.super) || planProducts;

  const isSuperAdminUser = Boolean(user && user.super);
  const isOwnerUser = Boolean(user?.isOwner);
  // God mode RBAC: só owner e super veem tudo sem filtro de permissões.
  const isGodMode = isSuperAdminUser || isOwnerUser;
  const canManageSubscriptionAccess = canManageSubscription(user);
  const isBlockedRoute =
    location.pathname === '/account-blocked' || location.pathname === '/conta-bloqueada';

  const blockedAccessAlert =
    '⚠️ ACESSO BLOQUEADO!\n\nSeu plano está vencido. Para acessar esta funcionalidade, por favor, regularize seu pagamento na página Financeiro.';

  /** Rotas permitidas com plano bloqueado: regularizar pagamento e perfil próprio. */
  const isSubscriptionPathAllowedWhenBlocked = (pathname) =>
    pathname === '/account-blocked' ||
    pathname === '/conta-bloqueada' ||
    pathname.startsWith('/profile') ||
    isSubscriptionAreaPath(pathname);
  // Avisos de trial/assinatura só para quem gerencia a assinatura (proprietário da conta)
  const canSeeSubscriptionWarnings = !isSuperAdminUser && canManageSubscription(user);
  // Trial encerrado derruba planActive, então a barra de aviso precisa de ramo próprio —
  // é ela que informa o encerramento nas rotas de assinatura ainda liberadas.
  const bannerVisible =
    canSeeSubscriptionWarnings && !isBlockedRoute && ((planActive && trialActive) || trialExpired);

  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [trialEndingModalOpen, setTrialEndingModalOpen] = useState(false);
  const [profileUrl, setProfileUrl] = useState(null);
  const [volume, setVolume] = useState(() => {
    const raw = localStorage.getItem('volume');
    const num = raw !== null && raw !== '' ? Number(raw) : NaN;
    return !Number.isFinite(num) ? 1 : Math.min(1, Math.max(0, num));
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [submenuPopoverAnchor, setSubmenuPopoverAnchor] = useState(null);
  const [submenuPopoverMenuKey, setSubmenuPopoverMenuKey] = useState(null);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const supportLinks = useSupportLinks();

  useEffect(() => {
    if (isMobile) {
      setDrawerExpanded(false);
      return;
    }
    if (!drawerInitialized.current && user) {
      drawerInitialized.current = true;
      setDrawerExpanded(user.defaultMenu !== 'closed');
    }
  }, [user, isMobile]);

  useEffect(() => {
    setExpandedMenus((prev) => ({
      ...prev,
      sistema: location.pathname.startsWith('/system') || location.pathname.startsWith('/sistema'),
      superAdmin: location.pathname.startsWith('/super-admin'),
      vendas: location.pathname.startsWith('/sales') || location.pathname.startsWith('/vendas'),
      marketing:
        location.pathname.startsWith('/marketing') || location.pathname.startsWith('/campanhas'),
      contatos:
        location.pathname.startsWith('/contacts') || location.pathname.startsWith('/contatos'),
      atendimentos:
        (location.pathname.startsWith('/tickets') &&
          !location.pathname.startsWith('/tickets/conversation')) ||
        location.pathname.startsWith('/chats'),
      automacoes: location.pathname.startsWith('/automations'),
      crm: location.pathname.startsWith('/crm'),
      tasks: location.pathname.startsWith('/tasks'),
      ferramentas: location.pathname.startsWith(APP_PATHS.TOOLS),
    }));
  }, [location.pathname]);

  useEffect(() => {
    if (user?.profileImage) {
      const nextUrl = `${backendUrl}/public/company${user.companyId}/profile-images/${user.profileImage}`;
      setProfileUrl((current) => (current !== nextUrl ? nextUrl : current));
    } else {
      setProfileUrl((current) => (current !== null ? null : current));
    }
  }, [user?.profileImage, user?.companyId]);

  // BLOQUEIO PARA PLANOS VENCIDOS (ignorado para Super Admin)
  useEffect(() => {
    if (isSuperAdminUser) return;

    // Se ainda está carregando informações do plano, não faz nada
    if (planLoading || loading) return;

    if (!planActive) {
      if (
        location.pathname.startsWith('/subscription/creditos') ||
        location.pathname.startsWith('/subscription/ai-credits')
      ) {
        history.replace(SUBSCRIPTION_PATH_BY_SECTION[SUBSCRIPTION_SECTIONS.FATURA]);
        return;
      }

      if (isSubscriptionAreaPath(location.pathname) && !canManageSubscriptionAccess) {
        history.push('/account-blocked');
        setBlockModalOpen(true);
        setTrialEndingModalOpen(false);
        return;
      }

      // Conta bloqueada: só planos/faturas; demais rotas → página de bloqueio.
      if (!isSubscriptionPathAllowedWhenBlocked(location.pathname)) {
        history.push('/account-blocked');
        setBlockModalOpen(true);
        setTrialEndingModalOpen(false);
      }
    }
  }, [
    planActive,
    planLoading,
    loading,
    location.pathname,
    history,
    isSuperAdminUser,
    canManageSubscriptionAccess,
  ]);

  // Remove push subscription quando conta bloqueada (fatura vencida) — evita notificações indevidas
  useEffect(() => {
    if (!isSuperAdminUser && !planActive) {
      unsubscribeAndUnregister();
    }
  }, [planActive, isSuperAdminUser]);

  // Usuário sem nenhuma permissão configurada → redireciona para página de aviso
  useEffect(() => {
    if (!user || loading || planLoading) return;
    if (user.super || user.isOwner) return;
    if (location.pathname === '/no-permission') return;
    if (location.pathname === '/profile') return;
    if (userPermissions.size === 0) {
      history.replace('/no-permission');
    }
  }, [user, loading, planLoading, userPermissions, location.pathname, history]);

  // Modal "período de teste acabando" — usa user.trial (SerializeUser do backend) como fonte de verdade
  useEffect(() => {
    if (!canSeeSubscriptionWarnings || !planActive) return;
    if (!user?.companyId) return;
    if (planLoading || loading) return;

    // Usa user.trial quando disponível (novo backend); fallback para dueDate durante migração
    const trial = user?.trial;
    const shouldShow = trial
      ? shouldShowTrialEndingModal(trial)
      : Boolean(user?.company?.dueDate && trialActive && trialExpiringSoon);

    if (!shouldShow) return;
    if (wasTrialEndingModalShownToday(user.companyId)) return;

    setTrialEndingModalOpen(true);
  }, [
    user?.companyId,
    user?.trial,
    user?.company?.dueDate,
    planActive,
    planLoading,
    loading,
    canSeeSubscriptionWarnings,
    trialActive,
    trialExpiringSoon,
  ]);

  const handleTrialEndingModalClose = () => {
    markTrialEndingModalShownToday(user?.companyId);
    setTrialEndingModalOpen(false);
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setUserMenuOpen(true);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
    setUserMenuOpen(false);
  };

  const handleOpenMyCompany = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.MY_COMPANY);
  };

  const handleOpenUserProfile = () => {
    handleUserMenuClose();
    history.push('/profile');
  };

  const handleOpenSettings = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.SETTINGS);
  };

  const handleOpenUsers = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.USERS);
  };

  const handleOpenRoles = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.ROLES);
  };

  const handleOpenSubscription = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.SUBSCRIPTION);
  };

  const handleLogoutClick = () => {
    handleLogout();
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const saveDrawerPreference = (expanded) => {
    if (!user?.id || isMobile) return;
    updateMyProfile({ defaultMenu: expanded ? 'open' : 'closed' }).catch(() => {});
  };

  const isSubItemDisabled = (parentItem, sub) => Boolean(sub.disabled ?? parentItem.disabled);

  const handleSubmenuItemSelect = (parentItem, sub) => {
    if (isSubItemDisabled(parentItem, sub)) {
      alert(blockedAccessAlert);
      return;
    }
    history.push(sub.path);
    setSubmenuPopoverAnchor(null);
    setSubmenuPopoverMenuKey(null);
    if (isMobile) setMobileOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Fechar o sheet "Mais" ao navegar
  useEffect(() => {
    setMoreSheetOpen(false);
  }, [location.pathname]);

  /**
   * Itens do sheet "Mais" — adicione novos objetos aqui para escalar o menu mobile.
   * Formato: { title, path, icon, permission? }
   *
   * Além dos atalhos exclusivos (Apps), o sheet repete os destinos que a tela
   * inicial oferece (Dashboard, Clientes, Serviços, CRM, Tarefas). A home é o
   * primeiro botão da barra, então sem essa repetição o usuário que navega pra
   * longe dela perde o caminho de volta pra esses módulos — a barra inferior só
   * tem 5 slots e nenhum sobra.
   *
   * `permission` segue a mesma regra do menu lateral: god mode vê tudo, o resto
   * depende do RBAC. Item sem `permission` aparece pra todos.
   */
  const mobileSheetItems = useMemo(() => {
    const items = [
      {
        title: 'Dashboard',
        path: '/dashboard',
        icon: <DashboardIcon />,
        permission: 'dashboard:read',
      },
      {
        title: 'Clientes',
        path: '/sales/clients',
        icon: <PersonIcon />,
        permission: 'clients:read',
      },
      {
        title: 'Serviços',
        path: '/sales/services',
        icon: <BuildIcon />,
        permission: 'services:read',
      },
      // Só owner/super (guard `isOwner` do backend): quem não tem god mode nunca vê.
      { title: 'Faturamento', path: APP_PATHS.BILLING, icon: <ReceiptIcon />, godOnly: true },
      { title: 'CRM', path: '/crm', icon: <ViewKanbanIcon />, permission: 'crm:read' },
      // Tarefas é módulo de plano: sem a flag não existe nem pra quem tem RBAC.
      ...(userCanUseTasks
        ? [{ title: 'Tarefas', path: '/tasks', icon: <TaskAltIcon />, permission: 'tasks:read' }]
        : []),
      { title: 'Apps', path: '/apps', icon: <AppsIcon /> },
    ];

    if (isGodMode) return items;
    return items.filter(
      (item) => !item.godOnly && (!item.permission || userPermissions.has(item.permission))
    );
  }, [isGodMode, userPermissions, userCanUseTasks]);

  /** Sheet "Mais": ativo se aberto OU se a rota atual pertence a um item do sheet */
  const isMoreActive = moreSheetOpen || mobileSheetItems.some((i) => isActivePath(i.path));

  // Detectar se está dentro de uma conversa de ticket (para ocultar menu mobile e header)
  const isInsideTicketConversation = /\/tickets\/conversation\/[^/]+$/i.test(location.pathname);

  // Detectar se está no Flow Builder (para ocultar menu lateral)
  const isFlowBuilderPage =
    location.pathname === '/automations/flowbuilder' ||
    location.pathname.startsWith('/automations/flowbuilder/');

  /** Página de conversas internas: sem menu mobile inferior (layout tipo messenger). */
  const isInternalChatsPage = location.pathname.startsWith('/chats');
  const hideMobileBottomNav =
    isInsideTicketConversation || isFlowBuilderPage || isInternalChatsPage;

  const staticMenuItems = useMemo(() => {
    // Página /chats: super sempre; owner só com chat interno no plano.
    const showInternalChatAdminPage =
      isSuperAdminUser || (isOwnerUser && !planLoading && planInternalChat);

    // Apps de produtividade da Loja: cada um entra com o app ativo (ou para o
    // super admin). Sem nenhum, o grupo Ferramentas nem aparece — grupo vazio
    // no menu é promessa sem nada dentro.
    const toolItems = [
      ...(isSuperAdminUser || hasApp('mind_map')
        ? [
            {
              title: i18n.t('mindMaps.menu'),
              path: APP_PATHS.MIND_MAPS,
              icon: <HubIcon />,
              permission: 'mind_maps:read',
            },
          ]
        : []),
    ];

    return [
      {
        title: 'Início',
        path: '/home',
        icon: <HomeIcon />,
        exact: true,
        // Sem permission RBAC: página inicial deve aparecer para TODOS os
        // usuários. Sem isso, o filtro (item sem permission → null) a esconde
        // de quem não é god mode.
        alwaysVisible: true,
      },
      {
        title: 'Dashboard',
        path: '/dashboard',
        icon: <DashboardIcon />,
        exact: true,
        disabled: !planActive,
        permission: 'dashboard:read',
      },
      {
        title: 'Conversas',
        path: '/tickets/conversation',
        icon: <ChatBubbleOutlineIcon />,
        disabled: !planActive,
        permission: 'tickets:read',
      },
      {
        title: 'CRM',
        path: '/crm',
        icon: <ViewKanbanIcon />,
        disabled: !planActive,
        permission: 'crm:read',
        ...(isGodMode ||
        userPermissions.has('crm:manage_pipelines') ||
        userPermissions.has('automations:manage_triggers')
          ? {
              subItems: [
                {
                  title: 'Pipeline',
                  path: '/crm/pipeline',
                  icon: <ViewKanbanIcon />,
                  permission: 'crm:read',
                },
                {
                  title: 'Funis',
                  path: '/crm/funnels',
                  icon: <AccountTreeIcon />,
                  permission: 'crm:manage_pipelines',
                },
              ],
            }
          : {}),
      },
      ...(isSuperAdminUser || (!planLoading && userCanUseTasks)
        ? [
            {
              title: 'Tarefas',
              path: '/tasks',
              icon: <TaskAltIcon />,
              disabled: !planActive,
              permission: 'tasks:read',
            },
          ]
        : []),
      {
        title: 'Atendimento',
        path: '/tickets',
        icon: <SupportAgentIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Mensagens rápidas',
            path: '/tickets/quick-messages',
            icon: <FlashOnIcon />,
            permission: 'quick_messages:read',
          },
          {
            title: 'Departamentos (fila)',
            path: '/tickets/queues',
            icon: <QueueIcon />,
            permission: 'queues:read',
          },
          {
            title: 'Tags',
            path: '/tickets/tags',
            icon: <LocalOfferIcon />,
            permission: 'tags:read',
          },
          ...(isSuperAdminUser || (!planLoading && planSchedules)
            ? [
                {
                  title: 'Mensagens agendadas',
                  path: '/tickets/schedules',
                  icon: <ScheduleIcon />,
                  permission: 'schedules:read',
                },
              ]
            : []),
          ...(showInternalChatAdminPage
            ? [
                {
                  title: 'Chats internos',
                  path: '/chats',
                  icon: <ForumIcon />,
                  // Sem permission key no catálogo RBAC: só owner (com plano) ou super.
                  alwaysVisible: true,
                },
              ]
            : []),
        ],
      },
      /**
       * Conexões saiu de dentro de Atendimento e virou item de primeiro nível:
       * era o que as pessoas mais tinham dificuldade de encontrar, escondido
       * entre tags, filas e mensagens rápidas.
       *
       * Sem submenu de propósito. Templates, saúde e custos são sempre de UMA
       * conexão, e um item de menu não tem como saber qual — cairia numa
       * qualquer. Por isso a tela se abre pelo menu do card, que já carrega o
       * número escolhido.
       */
      {
        title: 'Conexões',
        path: APP_PATHS.CHANNELS,
        // WhatsApp e não o hub genérico: é por esse nome que as pessoas
        // procuram, e o ícone é o que faz o item ser reconhecido de relance.
        icon: <WhatsAppIcon />,
        disabled: !planActive,
        permission: 'connections:read',
      },
      {
        title: 'Vendas',
        path: '/sales',
        icon: <BusinessCenterIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Clientes',
            path: '/sales/clients',
            icon: <PersonIcon />,
            permission: 'clients:read',
          },
          ...(isSuperAdminUser || (!planLoading && userCanUseProducts)
            ? [
                {
                  title: 'Produtos',
                  path: '/sales/products',
                  icon: <ShoppingCartIcon />,
                  permission: 'products:read',
                },
                {
                  title: 'Categorias',
                  path: '/sales/categories',
                  icon: <CategoryIcon />,
                  permission: 'products:manage_categories',
                },
                {
                  title: 'Atributos',
                  path: '/sales/attributes',
                  icon: <TuneIcon />,
                  permission: 'products:manage_attributes',
                },
              ]
            : []),
          {
            title: 'Serviços',
            path: '/sales/services',
            icon: <BuildIcon />,
            permission: 'services:read',
          },
          // Cobranças da empresa aos clientes dela. Sem `permission` de propósito:
          // o filtro abaixo só mostra subitem sem permission a owner/super, que é
          // o mesmo guard do backend (`isOwner`) e da rota (`ownerOnly`).
          {
            title: 'Faturamento',
            path: APP_PATHS.BILLING,
            icon: <ReceiptIcon />,
          },
        ],
      },
      ...(isSuperAdminUser || (!planLoading && userCanUseAgenda)
        ? [
            {
              title: 'Agenda',
              path: '/book/appointments',
              icon: <EventIcon />,
              disabled: !planActive,
              subItems: [
                {
                  title: 'Compromissos',
                  path: '/book/appointments',
                  icon: <EventAvailableIcon />,
                  permission: 'agenda:read',
                },
                {
                  title: 'Calendários',
                  path: '/book/calendars',
                  icon: <DateRangeIcon />,
                  permission: 'agenda:read',
                },
              ],
            },
          ]
        : []),
      {
        title: 'Marketing',
        path: '/marketing',
        icon: <CampaignIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'FollowUp',
            path: '/marketing/flowup',
            icon: <TrendingUpIcon />,
            permission: 'followup:read',
          },
          {
            title: 'Tempo sem resposta',
            path: '/marketing/no-response',
            icon: <TimerIcon />,
            permission: 'no_response:read',
          },
          {
            title: 'Campanhas por frase',
            path: '/marketing/campaign-by-phrase',
            icon: <MessageIcon />,
            permission: 'campaigns:read',
          },
          ...(isSuperAdminUser || hasApp('mass_dispatch')
            ? [
                {
                  title: 'Disparo em massa',
                  path: '/marketing/mass-shooting',
                  icon: <SendIcon />,
                  permission: 'mass_dispatch:read',
                },
              ]
            : []),
          ...(isSuperAdminUser || hasApp('smart_form')
            ? [
                {
                  title: 'SmartForms',
                  path: '/marketing/forms',
                  icon: <SmartFormIcon />,
                  permission: 'forms:read',
                },
              ]
            : []),
          ...(isSuperAdminUser || hasApp('group_schedule')
            ? [
                {
                  title: 'Agendamento para Grupos',
                  path: '/marketing/group-schedules',
                  icon: <CampaignOutlinedIcon />,
                  permission: 'group_schedules:read',
                },
              ]
            : []),
          ...(isSuperAdminUser || hasApp('meta_pixel')
            ? [
                {
                  title: 'Pixel do Meta',
                  path: '/marketing/pixel-meta',
                  icon: <SiMeta />,
                  permission: 'marketing:manage_pixels',
                },
              ]
            : []),
        ],
      },
      {
        title: 'Contatos',
        path: '/contacts',
        icon: <ContactsIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Listas segmentadas (tags)',
            path: '/contacts/contact-lists',
            icon: <ListIcon />,
            permission: 'contacts:group',
          },
          {
            title: 'Lista de contatos',
            path: '/contacts/import-contacts',
            icon: <ContactsIcon />,
            permission: 'contacts:read',
          },
          // {
          //   title: 'Configurações de Contatos',
          //   path: '/contacts/settings',
          //   icon: <SettingsIcon />,
          // },
        ],
      },
      {
        title: 'Automações',
        path: '/automations',
        icon: <ExtensionIcon />,
        disabled: !planActive,
        subItems: [
          {
            // Veio de CRM › Automações: com gatilhos de plataforma externa
            // (venda aprovada, pedido em preparação), deixou de ser assunto
            // de CRM. Ver docs/planos/plano-gatilhos-automacoes.md.
            title: 'Gatilhos',
            path: '/automations/triggers',
            icon: <BoltIcon />,
            permission: 'automations:manage_triggers',
          },
          ...(isSuperAdminUser || (!planLoading && planOpenAi)
            ? [
                {
                  title: 'IA Workflows',
                  path: '/automations/ia-workflows',
                  icon: <AccountTreeIcon />,
                  permission: 'ia:read',
                },
                {
                  title: 'Agentes de IA',
                  path: '/automations/agents',
                  icon: <SmartToyIcon />,
                  permission: 'ia:read',
                },
              ]
            : []),
          {
            title: 'Construtor de fluxo',
            path: '/automations/flowbuilders',
            icon: <AccountTreeIcon />,
            permission: 'flowbuilder:read',
          },
          ...(isSuperAdminUser || (!planLoading && planIntegrations)
            ? [
                {
                  title: 'Integrações',
                  path: '/automations/integration',
                  icon: <DeviceHubIcon />,
                  permission: 'integrations:read',
                },
              ]
            : []),
          // Conexões MCP é módulo de plano: sem a flag não existe nem pra
          // quem tem RBAC — mesmo padrão de Tarefas/Integrações.
          ...(isSuperAdminUser || (!planLoading && planMcp)
            ? [
                {
                  title: 'Conexões MCP',
                  path: '/automations/mcp-connections',
                  icon: <CableIcon />,
                  permission: 'integrations:mcp_read',
                },
              ]
            : []),
        ],
      },
      ...(toolItems.length
        ? [
            {
              title: i18n.t('mainDrawer.listItems.tools'),
              path: APP_PATHS.TOOLS,
              icon: <HandymanIcon />,
              disabled: !planActive,
              subItems: toolItems,
            },
          ]
        : []),
      {
        title: 'Apps',
        path: '/apps',
        icon: <AppsIcon />,
        disabled: !planActive,
      },
      ...(isSuperAdminUser
        ? [
            {
              title: 'White Label',
              path: '/super-admin',
              icon: <AdminPanelSettingsIcon />,
              disabled: !planActive,
              subItems: [
                { title: 'Empresas', path: '/super-admin/companies', icon: <BusinessIcon /> },
                { title: 'Faturas', path: '/super-admin/invoices', icon: <ReceiptIcon /> },
                {
                  title: 'Financeiro (Relatórios)',
                  path: '/super-admin/financial',
                  icon: <ReceiptIcon />,
                },
                { title: 'Planos', path: '/super-admin/plans', icon: <LoyaltyIcon /> },
                { title: 'Apps', path: '/super-admin/apps', icon: <AppsIcon /> },
                { title: 'Personalização', path: '/super-admin/whitelabel', icon: <PaletteIcon /> },
                {
                  title: 'Credenciais',
                  path: '/super-admin/payment-credentials',
                  icon: <VpnKeyIcon />,
                },
                { title: 'Configurações', path: '/super-admin/settings', icon: <SettingsIcon /> },
                {
                  title: 'Créditos de IA',
                  path: '/super-admin/ai-credits',
                  icon: <SmartToyIcon />,
                },
                {
                  title: 'Notificações',
                  path: '/super-admin/notifications',
                  icon: <NotificationsActiveIcon />,
                },
                {
                  title: 'Monitoramento',
                  path: '/super-admin/monitoring',
                  icon: <MonitorHeartIcon />,
                },
              ],
            },
          ]
        : []),
    ];
  }, [
    planActive,
    hasApp,
    location.pathname,
    isSuperAdminUser,
    isOwnerUser,
    isGodMode,
    user?.profile,
    planLoading,
    planOpenAi,
    planIntegrations,
    planSchedules,
    planInternalChat,
    planMcp,
    allowAiPlatformCredits,
    userPermissions,
  ]);

  // Exibe apenas itens cujas permissions o usuário possui.
  // Só owner/super (god mode) veem staticMenuItems direto; admin comum respeita RBAC.
  const menuItems = useMemo(() => {
    if (isGodMode) return staticMenuItems;
    return staticMenuItems
      .map((item) => {
        if (item.alwaysVisible) return item;
        if (item.subItems) {
          const allowedSubs = item.subItems.filter(
            (sub) => sub.alwaysVisible || (sub.permission && userPermissions.has(sub.permission))
          );
          if (allowedSubs.length === 0) return null;
          return { ...item, subItems: allowedSubs };
        }
        if (!item.permission) return null;
        return userPermissions.has(item.permission) ? item : null;
      })
      .filter(Boolean);
  }, [staticMenuItems, isGodMode, userPermissions]);

  const MenuItemWithTooltip = ({ path, icon, title, exact = false, disabled = false }) => {
    const isActive = exact
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/');

    const handleClick = (event) => {
      if (disabled) {
        event.preventDefault();
        // Mostra alerta ao tentar acessar com plano vencido
        alert(blockedAccessAlert);
        return;
      }
      // Compara com o caminho exato, e não com `isActive`: numa subrota
      // (`/connections/whatsapp-official`) o item fica ativo para aparecer
      // destacado, e amarrar a navegação nisso deixava o clique sem efeito —
      // o menu marcava a seção mas não voltava para ela.
      if (location.pathname !== path) {
        history.push(path);
      }
    };

    const item = (
      <ListItem
        button
        disabled={disabled}
        onClick={handleClick}
        className={`${classes.menuItem} ${isActive ? 'active' : ''}`}
      >
        <ListItemIcon className={classes.menuIcon}>{icon}</ListItemIcon>
        {showMenuLabels && <ListItemText primary={title} classes={{ primary: classes.menuText }} />}
      </ListItem>
    );

    if (showMenuLabels) {
      return item;
    }

    return (
      <Tooltip title={title} placement="right" arrow>
        {item}
      </Tooltip>
    );
  };

  const DrawerContent = () => (
    <div>
      <div className={classes.drawerHeader}>
        <div className={classes.drawerHeaderLogo}>
          <div className={classes.logo} onClick={() => history.push('/home')}>
            {!drawerExpanded && theme.appLogoFavicon ? (
              // Recolhido: ícone configurado no White Label (favicon), não a
              // logo completa espremida — mesmo padrão do Asaas.
              <img src={theme.appLogoFavicon} alt="Ícone" className={classes.companyLogoIcon} />
            ) : theme.appLogoLight || theme.calculatedLogoLight?.() ? (
              <img
                src={theme.calculatedLogoLight ? theme.calculatedLogoLight() : theme.appLogoLight}
                alt="Logo"
                className={classes.companyLogo}
              />
            ) : (
              <DashboardIcon className={classes.logoIcon} />
            )}
          </div>
        </div>
      </div>

      <div className={classes.sidebarContent}>
        <List className={classes.menuList}>
          <div className={classes.menuSectionLabel}>Principais</div>
          {menuItems.map((item) => {
            if (item.subItems) {
              const menuKey = SUBMENU_KEY_BY_PATH[item.path] || null;
              const isMenuActive =
                item.path === '/marketing'
                  ? location.pathname.startsWith('/marketing') ||
                    location.pathname.startsWith('/campanhas')
                  : item.path === '/tickets'
                    ? // Conversas é item próprio (fora de Atendimento) mas
                      // mora em /tickets/conversation — sem excluir esse
                      // prefixo, Atendimento ficava marcado ativo nela também.
                      (location.pathname.startsWith('/tickets') &&
                        !location.pathname.startsWith('/tickets/conversation')) ||
                      location.pathname.startsWith('/chats')
                    : location.pathname === item.path ||
                      location.pathname.startsWith(item.path + '/');
              const isExpanded = menuKey ? expandedMenus[menuKey] : false;
              const handleParentClick = (event) => {
                if (item.disabled) {
                  alert(blockedAccessAlert);
                  return;
                }
                // Grupo só abre os filhos: clicar no pai e ser levado embora tira
                // da pessoa a chance de escolher para onde ir.
                if (showMenuLabels) {
                  if (menuKey) {
                    setExpandedMenus((prev) => ({
                      ...prev,
                      [menuKey]: !prev[menuKey],
                    }));
                  }
                } else {
                  setSubmenuPopoverAnchor(event.currentTarget);
                  setSubmenuPopoverMenuKey(menuKey);
                }
              };
              const parentItem = (
                <ListItem
                  button
                  disabled={item.disabled}
                  onClick={handleParentClick}
                  className={`${classes.menuItem} ${isMenuActive ? 'active' : ''}`}
                >
                  <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                  {showMenuLabels && (
                    <>
                      <ListItemText primary={item.title} classes={{ primary: classes.menuText }} />
                      {isExpanded ? (
                        <ExpandLessIcon className={classes.menuItemExpandIcon} />
                      ) : (
                        <ExpandMoreIcon className={classes.menuItemExpandIcon} />
                      )}
                    </>
                  )}
                </ListItem>
              );
              return (
                <React.Fragment key={item.path}>
                  {showMenuLabels ? (
                    parentItem
                  ) : (
                    <Tooltip title={item.title} placement="right" arrow>
                      {parentItem}
                    </Tooltip>
                  )}
                  {showMenuLabels && isExpanded && (
                    <div className={classes.subMenuWrapper}>
                      {item.subItems.map((sub) => {
                        const isSubActive =
                          location.pathname === sub.path ||
                          location.pathname.startsWith(sub.path + '/');
                        const subDisabled = isSubItemDisabled(item, sub);
                        const handleSubClick = () => {
                          if (subDisabled) {
                            alert(blockedAccessAlert);
                            return;
                          }
                          history.push(sub.path);
                        };
                        return (
                          <ListItem
                            key={sub.path}
                            button
                            disabled={subDisabled}
                            onClick={handleSubClick}
                            className={`${classes.subMenuItem} ${isSubActive ? 'active' : ''}`}
                          >
                            {sub.icon && (
                              <ListItemIcon className={classes.subMenuItemIcon}>
                                {sub.icon}
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={sub.title}
                              classes={{ primary: classes.subMenuText }}
                            />
                          </ListItem>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            }
            return <MenuItemWithTooltip key={item.path} {...item} />;
          })}
        </List>

        <Menu
          anchorEl={submenuPopoverAnchor}
          open={Boolean(submenuPopoverAnchor)}
          onClose={() => {
            setSubmenuPopoverAnchor(null);
            setSubmenuPopoverMenuKey(null);
          }}
          anchorReference="anchorPosition"
          anchorPosition={
            submenuPopoverAnchor
              ? {
                  left: collapsedDrawerWidth,
                  top: submenuPopoverAnchor.getBoundingClientRect().top,
                }
              : undefined
          }
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            className: classes.submenuPopoverPaper,
            elevation: 8,
          }}
          transitionDuration={200}
        >
          {(() => {
            const popoverParent = menuItems.find(
              (i) => i.subItems && SUBMENU_KEY_BY_PATH[i.path] === submenuPopoverMenuKey
            );
            if (!popoverParent?.subItems) return null;
            return popoverParent.subItems.map((sub) => {
              const subDisabled = isSubItemDisabled(popoverParent, sub);
              const isSubActive =
                location.pathname === sub.path || location.pathname.startsWith(sub.path + '/');
              return (
                <MenuItem
                  key={sub.path}
                  disabled={subDisabled}
                  onClick={() => handleSubmenuItemSelect(popoverParent, sub)}
                  className={`${classes.submenuPopoverItem} ${isSubActive ? 'active' : ''}`}
                >
                  {sub.icon && (
                    <ListItemIcon className={classes.subMenuItemIcon}>{sub.icon}</ListItemIcon>
                  )}
                  {sub.title}
                </MenuItem>
              );
            });
          })()}
        </Menu>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      {/* Sidebar Desktop */}
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Botão de recolher/expandir — fora do papel do Drawer (que corta
          overflow em X) pra poder sair inteiro pra fora da borda, igual o
          padrão do Asaas (reto do lado que gruda na sidebar, pill do lado de
          fora). Ícone único que gira 180°, não troca de componente — anima
          suave em vez de trocar de figura instantâneo. */}
      {!isMobile && (
        <Tooltip title={drawerExpanded ? 'Recolher menu' : 'Abrir menu'} placement="right">
          <IconButton
            onClick={() => {
              const next = !drawerExpanded;
              setDrawerExpanded(next);
              saveDrawerPreference(next);
            }}
            classes={{ root: classes.drawerEdgeToggle }}
            size="small"
            disableRipple
          >
            <ChevronLeftIcon
              fontSize="small"
              className={drawerExpanded ? '' : classes.drawerEdgeToggleIconFlipped}
            />
          </IconButton>
        </Tooltip>
      )}

      {/* Sidebar Mobile */}
      <Drawer
        className={classes.mobileDrawer}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Banner de trial — fixo no topo do viewport, acima do header */}
      {bannerVisible && (
        <div className={classes.trialBannerWrapper}>
          <TrialBanner />
        </div>
      )}

      {/* Header - Sempre visível */}
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        className={classes.appBar}
        style={{ top: bannerVisible ? TRIAL_BANNER_HEIGHT : 0 }}
      >
        <Toolbar className={classes.toolbar}>
          {/* Seção Esquerda */}
          <div className={classes.headerLeft}>
            {/* Back Button - Visível apenas no Flow Builder */}
            {isFlowBuilderPage && (
              <IconButton
                className={classes.menuButton}
                onClick={() => history.push('/automations/flowbuilders')}
                edge="start"
                title="Voltar para lista de fluxos"
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            {/* Menu Button Mobile - Oculto só no Flow Builder; na conversa o voltar fica no ticker */}
            {!isFlowBuilderPage && (
              <IconButton className={classes.menuButton} onClick={handleDrawerToggle} edge="start">
                <MenuIcon />
              </IconButton>
            )}

            {/* Busca - Oculto no mobile */}
            <div
              className={classes.searchContainer}
              onClick={() => setSearchModalOpen(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                placeholder="Buscar conversas..."
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                readOnly
                style={{ cursor: 'pointer' }}
              />
              <Button className={classes.searchButton}>
                <SearchIcon style={{ fontSize: 18 }} />
              </Button>
            </div>

            {/* Logo - Visível apenas no mobile. Usa o ícone do PWA (quadrado),
                evitando que o logo largo esprema/esconda os botões do header. */}
            <div
              className={classes.mobileLogo}
              onClick={() => history.push('/home')}
              role="button"
              aria-label="Ir para a página inicial"
            >
              <img src={theme.appLogoFavicon || logo} alt="Logo" />
            </div>
          </div>

          {/* Seção Direita */}
          <div className={classes.headerRight}>
            {/* Chip de progresso da meta da equipe (CRM) */}
            <TeamGoalChip />

            {/* Aviso do próximo compromisso da Agenda */}
            <NextMeetingChip />

            {/* Créditos de IA (só owner de plano com crédito de plataforma) */}
            <AiCreditsBadge />

            {/* Botão Nova Conversa */}
            {canCreateTicket && (
              <Button
                variant="contained"
                onClick={() => setNewTicketModalOpen(true)}
                startIcon={<AddIcon style={{ fontSize: 18 }} />}
                style={{
                  // Mesma origem dos botões redondos ao lado: a tag "Botão
                  // primário (fundo)" do Personalizar, com a primária do tema
                  // como reserva.
                  backgroundColor: `var(--wl-btn-primary-bg, ${primaryColor})`,
                  color: 'var(--wl-btn-primary-text, #fff)',
                  borderRadius: 18,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 12,
                  padding: '4px 14px',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Nova conversa
              </Button>
            )}

            {/* Ajuda: WhatsApp do suporte e base de conhecimento */}
            <TopBarHelpMenu {...supportLinks} buttonClassName={classes.iconButtonCompact} />

            {/* Botão Refresh */}
            <IconButton
              className={classes.iconButtonCompact}
              onClick={handleRefreshPage}
              title="Atualizar"
            >
              <CachedIcon />
            </IconButton>

            {/* Sino / Notificações */}
            <NotificationsDrawer
              volume={volume}
              setVolume={setVolume}
              primaryColor={primaryColor}
            />

            {/* Avatar do Usuário */}
            <Avatar className={classes.iconButton} src={profileUrl} onClick={handleUserMenuClick}>
              {!profileUrl && <PersonIcon style={{ fontSize: 30 }} />}
            </Avatar>
          </div>
        </Toolbar>
      </AppBar>

      {/* Menu do Usuário */}
      <Menu
        anchorEl={anchorEl}
        open={userMenuOpen}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        PaperProps={{ className: classes.userMenuPaper }}
      >
        {/* Minha empresa - primeiro item; dados cadastrais só do dono (mesma regra da Assinatura) */}
        {canManageSubscription(user) && (
          <Box className={classes.userMenuProfile} onClick={handleOpenMyCompany}>
            <Box className={classes.userMenuCompanyAvatar}>
              <BusinessIcon style={{ fontSize: 20 }} />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.userMenuName}>
                {user?.company?.name || 'Minha empresa'}
              </Typography>
              <Typography className={classes.userMenuRole}>Minha empresa</Typography>
            </Box>
            <Box className={classes.userMenuEditChip}>
              <Typography className={classes.userMenuEditChipText}>Ver</Typography>
            </Box>
          </Box>
        )}

        {/* Card de perfil */}
        <Box className={classes.userMenuProfile} onClick={handleOpenUserProfile}>
          <Avatar className={classes.userMenuAvatar}>
            {user?.name ? user.name.charAt(0).toUpperCase() : <PersonIcon />}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.userMenuName}>{user?.name || 'Meu Perfil'}</Typography>
            <Typography className={classes.userMenuRole}>
              {user?.super
                ? 'Super Admin'
                : user?.profile === 'admin'
                  ? 'Administrador'
                  : 'Usuário'}
            </Typography>
          </Box>
          <Box className={classes.userMenuEditChip}>
            <Typography className={classes.userMenuEditChipText}>Editar</Typography>
          </Box>
        </Box>

        <Divider className={classes.userMenuDivider} />

        {/* Assinatura - só proprietário da conta (e super admin como vitrine) */}
        {canManageSubscription(user) && (
          <Box className={classes.userMenuAction} onClick={handleOpenSubscription}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#eff6ff' }}>
              <CreditCardIcon style={{ fontSize: 18, color: '#3b82f6' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Assinatura</Typography>
              <Typography className={classes.userMenuActionSubtitle}>Gerencie seu plano</Typography>
            </Box>
          </Box>
        )}

        {/* Usuários - visível se tiver users:read */}
        {(isGodMode || canReadUsers) && (
          <Box className={classes.userMenuAction} onClick={handleOpenUsers}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#ecfdf5' }}>
              <PeopleAltIcon style={{ fontSize: 18, color: '#059669' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Usuários</Typography>
              <Typography className={classes.userMenuActionSubtitle}>
                Gerencie sua equipe
              </Typography>
            </Box>
          </Box>
        )}

        {/* Permissões - owner e super (alinhado ao guard ownerOnly da rota) */}
        {Boolean(user?.isOwner || user?.super) && (
          <Box className={classes.userMenuAction} onClick={handleOpenRoles}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#e3f2fd' }}>
              <SecurityIcon style={{ fontSize: 18, color: '#1976d2' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Permissões</Typography>
              <Typography className={classes.userMenuActionSubtitle}>Perfis de acesso</Typography>
            </Box>
          </Box>
        )}

        {/* Configurações - god mode (owner/super) ou quem tem settings:read */}
        {(isGodMode || userPermissions.has('settings:read')) && (
          <Box className={classes.userMenuAction} onClick={handleOpenSettings}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#f5f3ff' }}>
              <SettingsIcon style={{ fontSize: 18, color: '#7c3aed' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Configurações</Typography>
              <Typography className={classes.userMenuActionSubtitle}>
                Preferências do sistema
              </Typography>
            </Box>
          </Box>
        )}

        {/* Sair */}
        <Box
          className={`${classes.userMenuAction} ${classes.userMenuActionLogout}`}
          onClick={handleLogoutClick}
        >
          <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#fef2f2' }}>
            <ExitToAppIcon style={{ fontSize: 18, color: '#ef4444' }} />
          </Box>
          <Box>
            <Typography
              className={`${classes.userMenuActionTitle} ${classes.userMenuActionLogoutTitle}`}
            >
              {isMobileSession ? 'Sair do app' : 'Sair'}
            </Typography>
            <Typography className={classes.userMenuActionSubtitle}>Encerrar sessão</Typography>
          </Box>
        </Box>
      </Menu>

      {/* Modal Nova Conversa */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          setNewTicketModalOpen(false);
          if (ticket?.uuid) {
            setTabOpen('open');
            setCurrentTicket(ticket);
          }
        }}
      />

      {/* Modal de Notificações */}
      <NotificationModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
      />

      {/* Modal de bloqueio (fatura vencida) */}
      <BlockedModal open={blockModalOpen} onClose={() => setBlockModalOpen(false)} />

      {/* Modal período de teste acabando — plano ativo; nunca quando bloqueado */}
      <TrialEndingModal
        open={trialEndingModalOpen && planActive && !blockModalOpen}
        onClose={handleTrialEndingModalClose}
        trial={user?.trial}
        dueDate={user?.company?.dueDate}
      />

      {/* PWA: dynamic head, install prompt, e aviso para desinstalar/reinstalar quando a versão do ícone sobe */}
      <PwaHeadUpdater />
      <PWAInstallPrompt />
      <PwaReinstallModal />

      {/* Modal de Busca de Conversas */}
      <SearchTicketModal open={searchModalOpen} onClose={() => setSearchModalOpen(false)} />

      {/* Widget global: todos os usuários com chat no plano (super ignora plano). Oculto em /chats (página full). */}
      <InternalChatWidget
        disabled={
          (!isSuperAdminUser && !planInternalChat) || location.pathname.startsWith('/chats')
        }
      />

      {/* Painel lateral de presença de usuários — god mode (owner/super) ou quem tem users:read */}
      {(isGodMode || userPermissions.has('users:read')) && <UsersPresencePanel />}

      {/* Conteúdo Principal: scroll só dentro do wrapper; global header e (em páginas) page header ficam fixos */}
      <main
        id="app-main-content"
        className={`${classes.content} ${bannerVisible ? classes.contentWithBanner : ''} ${!hideMobileBottomNav ? classes.contentWithMobileNav : ''}`}
      >
        <div
          className={`${classes.contentScrollWrapper} ${hideMobileBottomNav ? classes.contentScrollWrapperNoNavPadding : ''}`}
        >
          <div className={classes.contentScrollInner}>
            <Suspense
              fallback={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="280px"
                  width="100%"
                >
                  <CircularProgress size={36} />
                </Box>
              }
            >
              {children}
            </Suspense>
          </div>
        </div>
      </main>

      {/* Aviso de crédito de IA acabando — flutuante, só para o dono da conta */}
      <AiCreditsAlert />

      {/* Menu Mobile Fixo - Oculto no desktop, em conversa de ticket, Flow Builder e /chats */}
      {!hideMobileBottomNav && (
        <div className={classes.mobileBottomNav}>
          {/* Botão 1 - Tela inicial (o Dashboard passou para o sheet "Mais") */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/home') ? 'active' : ''}`}
            onClick={() => history.push('/home')}
          >
            <HomeIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Tela inicial</span>
          </div>

          {/* Botão 2 - Contatos (página Lista de contatos) */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/contacts/import-contacts') ? 'active' : ''}`}
            onClick={() => history.push('/contacts/import-contacts')}
          >
            <ContactsIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Contatos</span>
          </div>

          {/* Botão Home (Centro) - Tickets */}
          <div
            className={classes.mobileNavHomeBtn}
            onClick={() => history.push('/tickets/conversation')}
          >
            <ChatIcon className={classes.mobileNavHomeIcon} />
          </div>

          {/* Botão 4 - Conexões (somente com connections:read) */}
          {(isGodMode || userPermissions.has('connections:read')) && (
            <div
              className={`${classes.mobileNavItem} ${isActivePath(APP_PATHS.CHANNELS) ? 'active' : ''}`}
              onClick={() => history.push(APP_PATHS.CHANNELS)}
            >
              <WhatsAppIcon className={classes.mobileNavIcon} />
              <span className={classes.mobileNavLabel}>Conexões</span>
            </div>
          )}

          {/* Botão 5 - Mais (abre o sheet com itens extras) */}
          <div
            className={`${classes.mobileNavItem} ${isMoreActive ? 'active' : ''}`}
            onClick={() => setMoreSheetOpen((prev) => !prev)}
          >
            <MenuIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Mais</span>
          </div>
        </div>
      )}

      {/* Backdrop do sheet "Mais" */}
      {!hideMobileBottomNav && moreSheetOpen && (
        <div className={classes.mobileNavSheetBackdrop} onClick={() => setMoreSheetOpen(false)} />
      )}

      {/* Sheet "Mais" — grid de atalhos escalável */}
      {!hideMobileBottomNav && moreSheetOpen && (
        <div className={classes.mobileNavSheet}>
          <div className={classes.mobileNavSheetHandle} />
          <div className={classes.mobileNavSheetTitle}>Atalhos</div>
          <div className={classes.mobileNavSheetGrid}>
            {mobileSheetItems.map((item) => (
              <div
                key={item.path}
                className={`${classes.mobileNavSheetItem} ${isActivePath(item.path) ? 'active' : ''}`}
                onClick={() => {
                  history.push(item.path);
                  setMoreSheetOpen(false);
                }}
              >
                {item.icon}
                <span className={classes.mobileNavSheetItemLabel}>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoggedInLayout;
